import { useCallback } from 'react';
import useDeleteClip from '../../StudioClipUserList/hooks/useStudioClips/useDeleteClip';
import { UseTTSInputs } from '../../../context/clips/useTTS';
import { useCreateClipMutation } from './operation.generated';
import { getApolloMutationErrorMessage } from '../../../util/apolloError';
import {
  EventName,
  useEventsContext,
} from '../../../context/events/EventsContext';
import { toast } from '../../Toaster';

interface useSaveClipInputs extends UseTTSInputs {
  fileName: string;
}

const useSavePreviewClip = () => {
  const deleteClip = useDeleteClip();
  const [saveClipToProject] = useCreateClipMutation({});
  const [eventTracking] = useEventsContext();

  const saveClip = useCallback(
    async (inputs: useSaveClipInputs) => {
      const clipInputs = {
        clipId: inputs.clipId,
        fileName: inputs.fileName,
        projectId: inputs.projectId,
        speakerId: inputs.actor.id.toString(),
        speakerVariantId: inputs.actor.variant.id.toString(),
        text: inputs.textInput,
        textFormat: inputs.textFormat,
        ttsVersion: inputs.ttsVersion!,
      };
      const result = await saveClipToProject({
        variables: {
          inputs: clipInputs,
        },
      }).catch(error => {
        toast({
          message: getApolloMutationErrorMessage(error, 'Failed to save clip.'),
          type: 'error',
        });
        return null;
      });

      if (!result) {
        return;
      }

      const { data } = result;

      if (data?.createClip.success) {
        const handleUndoSave = () => {
          if (data?.createClip.clip) {
            deleteClip(data?.createClip.clip);
          }
        };

        // Track event even though user may click undo
        eventTracking.track({
          type: EventName.ClipSave,
          data: {
            clipId: inputs.clipId,
            projectId: inputs.projectId,
            speakerId: inputs.actor.id.toString(),
            speakerVariantId: inputs.actor.variant.id.toString(),
            ttsVersion: inputs.ttsVersion!,
          },
        });

        toast({
          message: `Clip saved to this project`,
          cta: {
            label: 'Undo',
            onClick: handleUndoSave,
          },
        });
      } else {
        toast({
          message: data?.createClip.message || 'Failed to save clip.',
          type: 'error',
        });
      }
    },
    [deleteClip, eventTracking, saveClipToProject]
  );

  return [saveClip];
};

export default useSavePreviewClip;
